// Pure helpers behind the oversight console's thread view — no React, so the
// client tests can hold them to the rules without a DOM.
//
// Voices: 'user' is the buyer, 'rep' the rep (from their phone, or from the
// console — metadata.via 'console'), 'agent' is Clyde (his yes_nudge and intro
// rows included), 'member' a stranger in the group, 'aside' a private 1:1 text
// between Clyde and the rep about this order (never in the group), anything else
// ours, and the pre-group-thread 'human:<id>' take-over rows read as the rep's voice.

export type Voice = "buyer" | "rep" | "clyde" | "member" | "aside" | "system";

export interface MessageLike {
  role: string | null;
  content?: string | null;
  metadata?: Record<string, unknown> | null;
}

function meta(msg: MessageLike): Record<string, unknown> {
  return (msg.metadata ?? {}) as Record<string, unknown>;
}

export function voiceOf(msg: MessageLike): Voice {
  const role = msg.role || "";
  if (role === "user") return "buyer";
  if (role === "agent") return "clyde";
  if (role === "rep" || role.startsWith("human:")) return "rep";
  if (role === "member") return "member";
  if (role === "aside") return "aside";
  return "system";
}

/** An aside's sender: Clyde (the rep check, his answers) or the rep (their 1:1 reply). */
export function asideFrom(msg: MessageLike): "clyde" | "rep" {
  return meta(msg).asideFrom === "rep" ? "rep" : "clyde";
}

/** The rep's name an aside was written under, else the thread's. */
export function asideRepName(msg: MessageLike, threadRepName?: string | null): string | null {
  const n = meta(msg).repName;
  return (typeof n === "string" && n) || threadRepName || null;
}

/**
 * The texts Clyde opens with the rep about a buyer (server mirror:
 * server/services/clydeAsides.ts ASIDE_OPENING_KINDS): a rep check, the
 * orientation, and every supervisor ping — a buyer's question, a held order, a
 * question nobody answered (Jon, 2026-10-21).
 */
export const ASIDE_OPENING_KINDS = ["rep_check", "orientation", "question", "hold", "nudge", "note"];

/**
 * Where an aside links. The buyer's thread holds one copy of each text Clyde
 * opens with the rep, linked to the rep's own 1:1 with Clyde, where the
 * back-and-forth lives ("side"); the opening text in that 1:1 links back to the
 * buyer's thread ("buyer").
 */
export function asideLink(msg: MessageLike): { to: "side" | "buyer"; conversationId: number; messageId: number | null } | null {
  if (msg.role !== "aside") return null;
  const m = meta(msg);
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const side = num(m.sideConversationId);
  if (side) return { to: "side", conversationId: side, messageId: num(m.sideMessageId) };
  const buyer = num(m.buyerConversationId);
  if (buyer && ASIDE_OPENING_KINDS.includes(String(m.kind))) return { to: "buyer", conversationId: buyer, messageId: num(m.buyerMessageId) };
  return null;
}

/** Clyde's private 1:1 with a rep: no Clyde thread of its own, and asides that are not the group's linked copies. */
export function isSideConversation(messages: MessageLike[], hasThread: boolean): boolean {
  return !hasThread && messages.some((m) => m.role === "aside" && asideLink(m)?.to !== "side");
}

/** The line under the group's copy of a rep check: who saw it, and that it went out 1:1. */
export function asideNote(msg: MessageLike, repName?: string | null): string {
  return `Only ${repName || "the rep"} sees this · 1:1 text, not in the group`;
}

/**
 * Whether the question an opening aside asked is still waiting: "open" while its
 * escalation is in the open list (the console's "needs your input"), "answered"
 * once it is not — whoever answered, from their phone or from here. Null for a
 * row that asked nothing (a reply, an answer, an older row with no escalation).
 */
export function asideStatus(msg: MessageLike, openEscalationIds: Set<number>): "open" | "answered" | null {
  if (msg.role !== "aside" || asideFrom(msg) !== "clyde") return null;
  const m = meta(msg);
  if (!ASIDE_OPENING_KINDS.includes(String(m.kind))) return null;
  const id = m.escalationId;
  if (typeof id !== "number" || !Number.isFinite(id) || id <= 0) return null;
  return openEscalationIds.has(id) ? "open" : "answered";
}

/** The label on the rep's side of their 1:1: their name, or that it was typed in this console for them. */
export function sideRepLabel(msg: MessageLike, repName: string): string {
  return sentFromConsole(msg) ? `${repName} (answered from the console)` : repName;
}

/** The side thread's compose placeholder: the admin answers as the rep would, by text. */
export function sideComposePlaceholder(repName: string | null): string {
  return `Answer for ${repName || "the rep"}, the way they'd text it ("yes", "the red ones")…`;
}

/** A row the rep sent from this console (or an old take-over), as opposed to from their phone. */
export function sentFromConsole(msg: MessageLike): boolean {
  return meta(msg).via === "console" || (msg.role || "").startsWith("human:");
}

/** The label a rep row carries: the name it went out under, else the thread's rep, else "Rep". */
export function repLabel(msg: MessageLike, repName?: string | null): string {
  const sentAs = meta(msg).sentAs;
  return (typeof sentAs === "string" && sentAs) || repName || "Rep";
}

/** A tapback (`metadata.kind === 'reaction'`) is a marker, never a bubble. */
export function isReactionRow(msg: MessageLike): boolean {
  return meta(msg).kind === "reaction";
}

/** Clyde's confirmation names the order it placed: metadata.orderId, else the text. */
export function orderIdOfMessage(msg: MessageLike): number | null {
  if (msg.role !== "agent") return null;
  const id = meta(msg).orderId;
  if (typeof id === "number" && Number.isFinite(id)) return id;
  const m = /order #(\d+)/i.exec(msg.content ?? "");
  return m ? parseInt(m[1], 10) : null;
}

/** The text split so each "order #N" can become a link: strings stay strings. */
export function splitOrderMentions(content: string): Array<string | { orderId: number; text: string }> {
  return content.split(/(order #\d+)/i).filter((part) => part.length > 0).map((part) => {
    const m = /^order #(\d+)$/i.exec(part);
    return m ? { orderId: parseInt(m[1], 10), text: part } : part;
  });
}

/** A recap the buyer or rep said yes to names the order it became (metadata.placedOrderId). */
export function placedOrderIdOf(msg: MessageLike): number | null {
  if (msg.role !== "agent") return null;
  const id = meta(msg).placedOrderId;
  return typeof id === "number" && Number.isFinite(id) ? id : null;
}

export function channelLabel(channel: string | null | undefined): string {
  return channel === "whatsapp" ? "WhatsApp" : "Text";
}

export function startedByLabel(createdBy: string | null | undefined): string {
  if (!createdBy) return "";
  if (createdBy === "inbound") return "Started by the buyer's text";
  if (createdBy.startsWith("admin:")) return "Started from the account list";
  return `Started by ${createdBy}`;
}
